import React, { useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import { CircularProgress } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { User } from 'types/User';

import { formatNumberThousandsSeparator } from '../helpers/FormattingHelpers';
import { LoggerListItem } from '../types/Logger';
import { FlagAsDamagedModalData } from '../types/Modal';
import { ScanBoxCommon } from '../types/Scan';
import { getFilteredLoggers, isMarkAsDamagedEnabled } from '../views/ScanOrBoxDetail/ScanOrBoxDetail.utils';
import Loader from './Loader';
import LoggerCheckTableHeaderRow from './LoggerCheckTableHeaderRow';
import LoggerCheckTableRow from './LoggerCheckTableRow';

interface LoadingState {
    isLoadingScanItem: boolean;
    isPendingDeletingAllChecks: boolean;
    isLoadingCheckForIds: string[];
    isLoadingDeleteForIds: string[];
    isImportOngoing: boolean;
}

type SetFlagAsDamagedModalData = (data: FlagAsDamagedModalData) => void;
type HandleRemoveLoggerApiCall = (list: Array<LoggerListItem>) => void;

interface Props {
    scanItemRes: ScanBoxCommon;
    loggerList: Array<LoggerListItem>;
    searchByLoggerId: string;
    currentUser: User;
    failedLoggerCheckCount: number;
    setFlagAsDamagedModalData: SetFlagAsDamagedModalData;
    handleRemoveLoggerApiCall: HandleRemoveLoggerApiCall;
    loadingState: LoadingState;
}

const LoggerCheckTable = (props: Props) => {
    const virtualListParentRef = useRef<HTMLDivElement>(null);

    const {
        scanItemRes,
        loggerList,
        searchByLoggerId,
        currentUser,
        failedLoggerCheckCount,
        loadingState,
        setFlagAsDamagedModalData,
        handleRemoveLoggerApiCall
    } = props;

    const filteredLoggers = useMemo(
        () => getFilteredLoggers(searchByLoggerId, loggerList),
        [searchByLoggerId, loggerList]
    );

    const getItemKey = useCallback((index: number) => filteredLoggers[index]?.loggerId ?? index, [filteredLoggers]);

    const getScrollElement = useCallback(() => virtualListParentRef.current ?? document.createElement('div'), []);

    const rowVirtualizer = useVirtualizer({
        count: filteredLoggers.length,
        estimateSize: () => 65,
        overscan: 10,
        getItemKey,
        getScrollElement
    });

    const virtualRowItems = rowVirtualizer.getVirtualItems();

    const scanBelongsToUserLocation = Boolean(scanItemRes?.locationName === currentUser.locationName);
    const isAnotherUserScanning = currentUser.username !== scanItemRes.createdByUsername;
    const scanItemComplete = scanItemRes?.statusName === 'COMPLETE';
    const hideScanningRowActions = scanItemComplete || isAnotherUserScanning || !scanBelongsToUserLocation;
    const markAsDamagedEnabled = isMarkAsDamagedEnabled(scanItemRes, currentUser);

    return (
        <div className="logger-table-wrapper">
            {Boolean(failedLoggerCheckCount) && (
                <div className="error-text margin-bottom">
                    <FontAwesomeIcon icon="exclamation-circle" className="margin-right" />
                    Please remove failed loggers ({failedLoggerCheckCount})
                </div>
            )}

            <div data-testid="logger-check-table" className="table-container logger-table">
                <LoggerCheckTableHeaderRow scanType={scanItemRes?.scanTypeName} hideActions={hideScanningRowActions} />

                <div className="rows-container">
                    <Loader size={20} overComponent={true} loading={loadingState.isPendingDeletingAllChecks} />

                    <div
                        className={clsx('virtualised-container', {
                            'flex-center': !virtualRowItems.length
                        })}
                        data-testid="virtualized-rows"
                        ref={virtualListParentRef}
                    >
                        {!virtualRowItems.length ? (
                            <>
                                {!loadingState.isImportOngoing && !loadingState.isLoadingScanItem && (
                                    <div data-testid="empty-row" className="empty-table-text-container">
                                        <DocumentScannerIcon />
                                        <div>Please scan loggers</div>
                                    </div>
                                )}
                                {loadingState.isImportOngoing && (
                                    <div data-testid="empty-table" className="empty-table-text-container">
                                        <CircularProgress data-testid="loading-spinner-empty" />
                                        <div>Hang tight — importing may take a few moments</div>
                                    </div>
                                )}
                            </>
                        ) : (
                            loadingState.isImportOngoing && (
                                <div className="row processing-row">
                                    <CircularProgress
                                        size="18px"
                                        className="margin-right-extra"
                                        data-testid="loading-spinner-row"
                                    />
                                    Hang tight — importing may take a few moments
                                </div>
                            )
                        )}

                        <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
                            {virtualRowItems.map((virtualRow) => {
                                const index = virtualRow.index;
                                const logger = filteredLoggers[index];

                                return (
                                    <div
                                        key={virtualRow.key}
                                        ref={rowVirtualizer.measureElement}
                                        data-index={index}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            transform: `translateY(${
                                                virtualRow.start - rowVirtualizer.options.scrollMargin
                                            }px)`
                                        }}
                                    >
                                        <LoggerCheckTableRow
                                            key={logger.loggerId}
                                            logger={logger}
                                            rowNumber={index + 1}
                                            scanType={scanItemRes?.scanTypeName}
                                            hideActions={hideScanningRowActions}
                                            isFlagAsDamagedEnabled={markAsDamagedEnabled}
                                            onFlagAsDamagedClick={() =>
                                                setFlagAsDamagedModalData({ type: 'flag', logger })
                                            }
                                            onDeleteClick={() => {
                                                handleRemoveLoggerApiCall([logger]);
                                            }}
                                            isDeleting={loadingState.isLoadingDeleteForIds.includes(logger.loggerId)}
                                            isLoading={loadingState.isLoadingCheckForIds.includes(logger.loggerId)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="footer-row flex-space-between">
                    <strong data-testid="item-quantity">
                        {formatNumberThousandsSeparator(loggerList.length)} items
                    </strong>
                    <div data-testid="label-container" className="flex">
                        {markAsDamagedEnabled && !hideScanningRowActions && (
                            <div className="margin-right-extra">
                                <FontAwesomeIcon icon="link-horizontal-slash" className="margin-right" />
                                <strong>Flag as Damaged</strong>
                            </div>
                        )}
                        {!hideScanningRowActions && (
                            <div>
                                <FontAwesomeIcon icon="trash" className="margin-right" />
                                <strong>Remove item</strong>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoggerCheckTable;
